import React, { Dispatch, SetStateAction, useState } from 'react';
import { PrimaryButton } from '../../../../../components/Buttons';
import { AdsTemplate } from '../../../../../types/IDiy';
import { Brand } from '../../../../../types';
import { archiveCampaignTemplate } from '../../../../../services/diy';
import AdsTemplateListTable from '../../../../../components/Tables/TemplateList/AdsTemplateListTable';
import { DrawerMenu } from '../../../../../components/Menu';
import CircularLoading from '../../../../../components/CircularLoading';
import FacebookAdDetails from '../../../../../components/Details/Ads/FacebookAdDetails';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';

interface TemplatesProps {
  brand: Brand;
  templates: AdsTemplate[];
  setTemplates: Dispatch<SetStateAction<AdsTemplate[]>>;
  loading: boolean;
  setOpenForm: Dispatch<SetStateAction<boolean>>;
  setFormAction: Dispatch<SetStateAction<'create' | 'edit' | 'duplicate'>>;
  selectedTemplate?: AdsTemplate;
  setSelectedTemplate?: Dispatch<SetStateAction<AdsTemplate | null>>;
}

const Templates: React.FC<TemplatesProps> = ({
  brand,
  templates,
  setTemplates,
  loading,
  setOpenForm,
  setFormAction,
  selectedTemplate,
  setSelectedTemplate,
}) => {
  const theme = useTheme();
  const xsOnly = useMediaQuery(theme.breakpoints.only('xs'));

  const [openPreview, setOpenPreview] = useState<boolean>(false);
  const [archiveLoading, setArchiveLoading] = useState<boolean>(false);
  const [confirmArchive, setConfirmArchive] = useState<boolean>(false);

  const handleOpenPreview = (template: AdsTemplate) => {
    setSelectedTemplate(template);
    setOpenPreview(true);
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setConfirmArchive(false);
  };

  const handleArchiveTemplate = async () => {
    try {
      setArchiveLoading(true);

      const response = await archiveCampaignTemplate(selectedTemplate?._id);

      const temp = templates?.filter(
        (template: AdsTemplate) => template?._id !== response?.data?._id,
      );

      setTemplates(temp);
      handleClosePreview();
    } catch (error: any) {
      console.log(error.message);
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleOnClickCreateTemplate = () => {
    setFormAction('create');
    setOpenForm(true);
    setSelectedTemplate(null);
  };

  const handleOnClickEditTemplate = (template: AdsTemplate) => {
    setFormAction('edit');
    setOpenForm(true);
    setSelectedTemplate(template);
  };

  const handleOnClickDuplicateTemplate = (template: AdsTemplate) => {
    setFormAction('duplicate');
    setOpenForm(true);
    setSelectedTemplate(template);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <CircularLoading loading={loading} />

        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PrimaryButton
            title="Create Template"
            type="button"
            handleOnClick={handleOnClickCreateTemplate}
          />
        </Grid>

        <Grid item xs={12}>
          <AdsTemplateListTable
            templates={templates}
            loading={loading}
            onOpenPreview={handleOpenPreview}
          />
        </Grid>
      </Grid>

      <DrawerMenu
        anchor="right"
        open={openPreview}
        onClose={handleClosePreview}
      >
        <FacebookAdDetails
          brand={brand?.__type === 'location' ? brand?.franchisor : brand}
          templateId={selectedTemplate?._id}
          isTemplate
          withAction
          onArchive={handleArchiveTemplate}
          archiveLoading={archiveLoading}
          onClose={handleClosePreview}
          isMobile={xsOnly}
          confirmArchive={confirmArchive}
          setConfirmArchive={setConfirmArchive}
          onEditTemplate={handleOnClickEditTemplate}
          onDuplicateTemplate={handleOnClickDuplicateTemplate}
        />
      </DrawerMenu>
    </Box>
  );
};

export default Templates;
